import React from "react";
import { getAlertVlmVerdict } from "./AlertsUtils/vlmFilters";

/**
 * Per-row DoubleTake verdict indicators (ENG-1756).
 *
 * Only alerts the verifier actually classified get an indicator: a `passed`
 * verdict reads "Likely TP", `suppressed` reads "Likely FP", and everything
 * else (unsorted: not_evaluated, unable_to_evaluate, no verdict yet) shows
 * nothing in the condensed view and a bold N/A in the detailed view, whose
 * column needs something in every cell. The reason an alert is unsorted stays
 * in the selected-alert banner, which has room for it.
 */

export const VLM_TP_COLOR = "#2E7D32";
// Same red as the False outcome button and the detection cancel icon.
export const VLM_FP_COLOR = "#D32F2F";

// Not a verdict: unsorted, unavailable, disabled or still pending. Actuate navy
// at ~70% on white. One value for every non-verdict — the N/A in the DT Verdict
// column, the selected-alert banner and the clip badge all read it from here,
// so a non-verdict never borrows a verdict's colour or drifts to a second grey.
export const VLM_NEUTRAL_COLOR = "#4D5E7B";

/**
 * The one colour for an alert's DoubleTake state, shared by the tiles, the row
 * chip, the verdict column, the selected-alert banner and the clip badge. Red
 * means "likely false positive" everywhere and nothing else.
 */
export const getVlmVerdictColor = (row: any): string => {
  const verdict = getAlertVlmVerdict(row);
  if (verdict === "passed") return VLM_TP_COLOR;
  if (verdict === "filtered") return VLM_FP_COLOR;
  return VLM_NEUTRAL_COLOR;
};

export const VLM_VERDICT_LABELS = {
  passed: "Likely TP",
  filtered: "Likely FP",
} as const;

/**
 * Condensed view: a chip that sits next to the metric chip (Intruder, Vehicle).
 * Renders nothing for an unsorted alert.
 */
export const VlmVerdictChip = ({ row }: { row: any }) => {
  const verdict = getAlertVlmVerdict(row);
  if (!verdict) {
    return null;
  }
  const color = verdict === "passed" ? VLM_TP_COLOR : VLM_FP_COLOR;
  const label = VLM_VERDICT_LABELS[verdict];
  return (
    <span
      className="inline-flex items-center rounded px-1.5 py-0.5 font-mulish text-[10px] font-bold leading-none"
      style={{
        color,
        backgroundColor: `${color}1A`,
        border: `1px solid ${color}59`,
      }}
    >
      {label}
    </span>
  );
};

/**
 * Detailed view: the DT Verdict column's contents — the label on its own, or a
 * bold N/A when the alert is unsorted. Text only, no icon (the Detection
 * column next to it is already icon-led).
 */
export const VlmVerdictCell = ({ row }: { row: any }) => {
  const verdict = getAlertVlmVerdict(row);
  if (!verdict) {
    return (
      <span
        className="font-mulish text-[11.5px] font-bold"
        style={{ color: VLM_NEUTRAL_COLOR }}
        aria-label="No DOUBLETAKE verdict"
      >
        N/A
      </span>
    );
  }
  return (
    <span
      className="whitespace-nowrap font-mulish text-[11.5px] font-bold"
      style={{ color: verdict === "passed" ? VLM_TP_COLOR : VLM_FP_COLOR }}
    >
      {VLM_VERDICT_LABELS[verdict]}
    </span>
  );
};
